/**
 * Genera un modelo JSON completo para reservas a partir de los datos de entrada
 * @param {Object} bookingData - Datos de la reserva (pasajeros y contacto)
 * @param {Object} flightInfo - Información de vuelos (token, solución, journeys)
 * @param {Object} selectedBaggage - Información de equipaje seleccionado con services[i].fee para calcular el monto
 * @returns {Object} Modelo JSON de respuesta (sin el wrapper "query")
 */
export function generateBookingModel(bookingData, flightInfo, selectedBaggage) {
  const travellers = mapPassengers(bookingData.passengers);
  const holder = createHolder(bookingData.contactInfo.data, bookingData.passengers);
  const ancillaries = extractAncillaries(selectedBaggage, flightInfo.token);
  const fees = extractFees(selectedBaggage);

  return {
    token: flightInfo.token,
    solutions: [
      {
        journeys: [flightInfo.journey00, flightInfo.journey01],
        id: flightInfo.solutionID0,
      },
    ],
    travellers,
    holder,
    ancillaries,
    fees,
  };
}

/**
 * Convierte los datos de pasajeros al formato requerido para travellers
 * @param {Array} passengersData - Array de datos de pasajeros
 * @returns {Array} Array de objetos traveller formateados
 */
function mapPassengers(passengersData) {
  return passengersData
    .filter((passenger) => passenger.personalInfo)
    .map((passenger, index) => {
      const { personalInfo } = passenger;
      const title = personalInfo.gender === 'Female' ? 'Mrs' : 'Mr';
      const type = getPassengerType(personalInfo);
      const documents = createDocuments(personalInfo);
      const contact = createEmergencyContact(passenger);
      const birthDate = getValidBirthDate(personalInfo.birthDate);

      const traveller = {
        type,
        title,
        name: personalInfo.name || '',
        documents,
        surnames: (personalInfo.lastName || '').split(' ').filter(Boolean),
        gender: personalInfo.gender === 'Female' ? 'Female' : 'Male',
        birthDate,
      };

      if (contact) traveller.contact = contact;

      return traveller;
    });
}

/**
 * Determina el tipo de pasajero basado en el campo passengerType
 * @param {Object} personalInfo - Información personal del pasajero
 * @returns {string} Tipo de pasajero (ADT, CHD, INF)
 */
function getPassengerType(personalInfo) {
  const typeMap = {
    adulto: 'ADT',
    adt: 'ADT',
    niño: 'CHD',
    nino: 'CHD',
    chd: 'CHD',
    bebé: 'INF',
    bebe: 'INF',
    inf: 'INF',
  };

  return typeMap[personalInfo.passengerType?.toLowerCase()] || personalInfo.type || 'ADT';
}

/**
 * Crea los documentos del pasajero si la información está disponible
 * @param {Object} personalInfo - Información personal del pasajero
 * @returns {Array} Array de documentos
 */
function createDocuments(personalInfo) {
  if (!personalInfo.documentNumber || !personalInfo.documentType) return [];
  return [
    {
      number: personalInfo.documentNumber,
      type: mapDocumentType(personalInfo.documentType),
    },
  ];
}

/**
 * Crea el contacto de emergencia si existe
 * @param {Object} passenger - Datos del pasajero
 * @returns {Object|null} Objeto de contacto de emergencia o null si no existe
 */
function createEmergencyContact(passenger) {
  if (!passenger.emergencyContact?.name) return null;

  const phones = [];
  if (passenger.emergencyContact.phone?.numero) {
    phones.push({
      countryPref: mapCountryCodeToPrefix(passenger.emergencyContact.phone.codigo || '54'),
      number: passenger.emergencyContact.phone.numero,
    });
  }

  return {
    type: 'Emergency',
    name: passenger.emergencyContact.name,
    surnames: (passenger.emergencyContact.lastName || '').split(' ').filter(Boolean),
    phones,
    country: passenger.emergencyContact.country || '',
  };
}

/**
 * Asegura que birthDate sea un string ISO 8601 válido
 * @param {string} dateString - Cadena de fecha
 * @returns {string} Fecha válida en formato ISO 8601
 */
function getValidBirthDate(dateString) {
  if (isValidDate(dateString)) return dateString;

  const defaultDate = new Date();
  defaultDate.setFullYear(defaultDate.getFullYear() - 20);
  return defaultDate.toISOString().split('T')[0];
}

/**
 * Crea un objeto holder a partir de los datos de contacto
 * @param {Object} contactData - Datos de contacto
 * @param {Array} passengers - Array de pasajeros para extraer documentos del titular
 * @returns {Object} Objeto holder formateado
 */
function createHolder(contactData, passengers = []) {
  const firstPassenger = passengers[0]?.personalInfo;
  const documents = createDocuments(firstPassenger || {});
  const birthDateStr = getValidBirthDate(firstPassenger?.birthDate, 30);
  const phones = createPhones(contactData);

  return {
    name: contactData.nombre || '',
    surnames: (contactData.apellidos || '').split(' ').filter(Boolean),
    contact: {
      mails: [contactData.email || 'ejemplo@email.com'],
      phones,
    },
    documents,
    birthDate: birthDateStr,
    fiscalData: {
      documentType: firstPassenger?.documentType || '',
      documentNumber: firstPassenger?.documentNumber || '',
      cuitCuil: firstPassenger?.cuitCuil || '',
    },
  };
}

/**
 * Crea los teléfonos del holder asegurando que tengan valores válidos
 * @param {Object} contactData - Datos de contacto
 * @returns {Array} Array de teléfonos
 */
function createPhones(contactData) {
  const phones = [];

  if (contactData?.telefono1) {
    phones.push({
      countryPref: String(contactData.telefono1.codigo || '54'),
      number: contactData.telefono1.numero || '0000000000',
    });
  }

  if (contactData?.telefono2) {
    phones.push({
      countryPref: String(contactData.telefono2.codigo || '54'),
      number: contactData.telefono2.numero || '0000000000',
    });
  }

  return phones.length > 0
    ? phones
    : [
        {
          countryPref: '54',
          number: '0000000000',
        },
      ];
}

/**
 * Extrae los ancillaries del objeto selectedBaggage
 * @param {Object} selectedBaggage - Objeto con el equipaje seleccionado
 * @param {string} token - Token de la reserva
 * @returns {Array} Array de objetos ancillary
 */
function extractAncillaries(selectedBaggage, token) {
  if (!selectedBaggage) return [];

  const ancillaries = [];
  const passengerTypes = Object.keys(selectedBaggage);

  passengerTypes.forEach((passengerType) => {
    const items = selectedBaggage[passengerType];
    if (!Array.isArray(items)) return;

    const [type, index] = passengerType.split('_');
    const paxRef = getPaxReference(passengerType, passengerTypes);

    items.forEach((item) => {
      if (item.id) {
        ancillaries.push({
          pax_references: [paxRef],
          total: item.total,
          price: item.price,
          base: item.base,
          currency: item.currency,
          short_name: item.short_name,
          id: `${token}-${item.id.split('-')[1]}`,
        });
      }
    });
  });

  return ancillaries;
}

/**
 * Determina la referencia del pasajero (T1, T2, etc.)
 * @param {string} passengerType - Tipo de pasajero (adult_0, child_1, etc.)
 * @param {Array} passengerTypes - Lista de tipos de pasajeros
 * @returns {string} Referencia del pasajero
 */
function getPaxReference(passengerType, passengerTypes) {
  const [type, index] = passengerType.split('_');
  const adultCount = passengerTypes.filter((k) => k.startsWith('adult')).length;
  const childCount = passengerTypes.filter((k) => k.startsWith('child')).length;

  if (type === 'adult') return `T${parseInt(index) + 1}`;
  if (type === 'child') return `T${adultCount + parseInt(index) + 1}`;
  if (type === 'infant') {
    return `T${adultCount + childCount + parseInt(index) + 1}`;
  }
  return '';
}

/**
 * Extrae las tarifas (fees) del objeto selectedBaggage
 * @param {Object} selectedBaggage - Objeto con el equipaje seleccionado
 * @returns {Array} Array de objetos fee
 */
function extractFees(selectedBaggage) {
  if (!selectedBaggage) return [];

  return Object.entries(selectedBaggage)
    .map(([passengerType, items]) => {
      if (!Array.isArray(items)) return null;

      const [type, index] = passengerType.split('_');
      const paxType = type === 'adult' ? 'ADT' : type === 'child' ? 'CHD' : 'INF';
      const feeAmount = items.reduce((sum, item) => sum + (item.fee || 0), 0);

      return {
        pax_type: `${paxType}-${index}`,
        amount: Math.round(feeAmount * 100) / 100,
      };
    })
    .filter(Boolean);
}

/**
 * Mapea el tipo de documento al formato requerido
 * @param {string} docType - Tipo de documento original
 * @returns {string} Tipo de documento en formato requerido
 */
function mapDocumentType(docType) {
  const documentMap = {
    dni: 'NI',
    pasaporte: 'PP',
    cedula: 'CT',
    passport: 'PP',
    nationalid: 'NI',
    residentcard: 'TR',
    frequentflyer: 'FF',
    census: 'CE',
    drivinglicense: 'DL',
    resident: 'TR',
    license: 'DL',
    carnet: 'DL',
  };

  return documentMap[docType?.toLowerCase()] || 'NI';
}

/**
 * Mapea el código de país al prefijo telefónico
 * @param {string} countryCode - Código de país
 * @returns {string} Prefijo telefónico
 */
function mapCountryCodeToPrefix(countryCode) {
  const countryCodeMapping = {
    AR: '+54',
    ES: '+34',
    CL: '+56',
    MX: '+52',
    BR: '+55',
    UY: '+598',
    PY: '+595',
    BO: '+591',
    PE: '+51',
    CO: '+57',
    VE: '+58',
    EC: '+593',
    GY: '+592',
    SR: '+597',
    FR: '+33',
    IT: '+39',
    DE: '+49',
    GB: '+44',
  };

  return String(countryCodeMapping[countryCode] || countryCode || '54');
}

/**
 * Verifica si una cadena es una fecha ISO 8601 válida
 * @param {string} dateString - Cadena de fecha a validar
 * @returns {boolean} True si es una fecha válida
 */
function isValidDate(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}
